import type { DeliveryPlan, PlannedShipment, Shipment, ShipmentProduct, ShipmentStatus } from '@/api/types'

export const SHIPMENT_STATUS_VALUES: ShipmentStatus[] = ['booking', 'booked', 'dispatched', 'delivered', 'failed', 'cancelled', 'returned']

export const SHIPMENT_STATUS_LABELS: Record<ShipmentStatus, string> = {
  booking: 'Bokas',
  booked: 'Bokad',
  dispatched: 'Skickad',
  delivered: 'Levererad',
  failed: 'Misslyckad',
  cancelled: 'Avbruten',
  returned: 'Returnerad'
}

export const SHIPMENT_STATUS_STYLES: Record<ShipmentStatus, string> = {
  booking: 'bg-amber-100 text-amber-800',
  booked: 'bg-blue-100 text-blue-800',
  dispatched: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-700',
  returned: 'bg-orange-100 text-orange-800'
}

export function formatShipmentNumber(shipmentId: number): string {
  return `Leverans nr ${shipmentId}`
}

// Bara bokade försändelser har en fraktsedel att skriva ut.
export function hasShipmentLabel(shipment: Shipment): boolean {
  return shipment.label_url !== null
}

const REMOVABLE_STATUSES: ShipmentStatus[] = ['booking', 'booked', 'failed']
const UNDELIVERED_STATUSES: ShipmentStatus[] = ['booking', 'booked', 'dispatched', 'failed']

export type ShipmentAction = 'remove' | 'write_off' | 'customer_return'

export const SHIPMENT_ACTION_LABELS: Record<ShipmentAction, string> = {
  remove: 'Ta bort',
  write_off: 'Skriv av',
  customer_return: 'Returnerad av kunden'
}

export function canRemoveShipment(shipment: Shipment): boolean {
  return REMOVABLE_STATUSES.includes(shipment.status)
}

export function canWriteOffShipment(shipment: Shipment): boolean {
  return shipment.status === 'dispatched'
}

export function canMarkReturnedByCustomer(shipment: Shipment): boolean {
  return shipment.status === 'delivered'
}

export function isReturnedByCustomer(shipment: Shipment): boolean {
  return shipment.status === 'returned' && shipment.returned_by_customer_at !== null
}

const UNBILLED_STATUSES: ShipmentStatus[] = ['cancelled', 'returned']
const UNSENT_STATUSES: ShipmentStatus[] = ['booking', 'booked']

export const DUE_DATE_DEFAULT_TEXT = 'Enligt standardregeln'
export const INVOICE_DATE_DEFAULT_TEXT = 'Dagen fakturan skapas'

// Faktureringen går att ändra tills fakturan skapats. En borttagen eller returnerad leverans faktureras inte.
export function canChangeShipmentBilling(shipment: Shipment, hasInvoice: boolean): boolean {
  return !hasInvoice && !UNBILLED_STATUSES.includes(shipment.status)
}

// Bara en leverans som inte lämnats in kan faktureras före inlämningen.
export function canInvoiceBeforeDispatch(shipment: Shipment): boolean {
  return UNSENT_STATUSES.includes(shipment.status)
}

export function getOverrideDateText(value: string | null, defaultText: string): string {
  if (value === null) {
    return defaultText
  }

  return `${value} (satt för hand)`
}

export function getInvoiceTimingText(shipment: Shipment): string {
  if (shipment.invoice_before_dispatch) {
    return 'Före inlämning (satt för hand)'
  }

  return 'När PostNord tagit emot paketet'
}

export function getShipmentAction(shipment: Shipment): ShipmentAction | null {
  if (canRemoveShipment(shipment)) {
    return 'remove'
  }

  if (canWriteOffShipment(shipment)) {
    return 'write_off'
  }

  if (canMarkReturnedByCustomer(shipment)) {
    return 'customer_return'
  }

  return null
}

export function formatShipmentProducts(products: ShipmentProduct[]): string {
  return products.map((product) => `${product.quantity}× ${product.name}`).join(', ')
}

function describeUndelivered(shipments: Shipment[]): string {
  const sequences = shipments
    .filter((shipment) => UNDELIVERED_STATUSES.includes(shipment.status))
    .map((shipment) => shipment.sequence)
    .sort((a, b) => a - b)

  if (sequences.length === 0) {
    return ''
  }

  if (sequences.length === 1) {
    return `Leverans ${sequences[0]} är inte levererad än. `
  }

  return `Leverans ${sequences.slice(0, -1).join(', ')} och ${sequences[sequences.length - 1]} är inte levererade än. `
}

export function getCreateShipmentConfirmMessage(next: PlannedShipment, shipments: Shipment[]): string {
  const undelivered = describeUndelivered(shipments)
  const products = formatShipmentProducts(next.products)

  return `${undelivered}Skapa leverans ${next.sequence} nu (${products})? Den bokas hos PostNord vid nästa körning och faktureras när paketet lämnats in.`
}

export function getRemoveShipmentConfirmMessage(shipment: Shipment): string {
  return `Ta bort leverans ${shipment.sequence}? Bokningen avbokas hos PostNord och paketet får inte lämnas in. Leveransen räknas inte längre, så en ny skapas automatiskt när den är aktuell. Stäng av leveranserna först om kunden inte ska ha någon ny.`
}

export function getWriteOffShipmentConfirmMessage(shipment: Shipment): string {
  return `Skriv av leverans ${shipment.sequence}? Gör det bara om paketet lämnats in hos PostNord men inte kommit fram. Leveransen räknas inte längre, så en ny skapas automatiskt när den är aktuell. Stäng av leveranserna först om kunden inte ska ha någon ny.`
}

export function getCustomerReturnConfirmMessage(shipment: Shipment): string {
  return `Markera leverans ${shipment.sequence} som returnerad av kunden? Gör det när kunden själv skickat tillbaka paketet och det har kommit fram till oss. Leveransen räknas inte längre och faktureras inte, och ingen ny leverans skickas automatiskt.`
}

export function getPausedDeliveryNotice(plan: DeliveryPlan): string | null {
  if (plan.paused_by?.by_customer) {
    return `Leverans ${plan.paused_by.sequence} returnerades av kunden. Ingen ny leverans skickas automatiskt. Skapa en ny leverans om kunden ska få en, eller säg upp abonnemanget.`
  }

  if (plan.paused_by) {
    return `Leverans ${plan.paused_by.sequence} returnerades av PostNord. Ingen ny leverans skickas automatiskt. Skapa en ny leverans när kunden ska få paketet.`
  }

  if (plan.overdue_hold) {
    return `Kunden har ${plan.overdue_hold.invoices} obetalda fakturor efter förfallodagen. Ingen ny leverans skickas automatiskt förrän färre än två är obetalda. Skapa leveransen för hand om kunden ändå ska få den.`
  }

  return null
}

export function getPlannedDeliveryText(plan: DeliveryPlan, today: string): string | null {
  if (!plan.next || !plan.planned_on) {
    return null
  }

  if (plan.planned_on <= today) {
    return `Nästa leverans (nr ${plan.next.sequence}) skapas vid nästa körning.`
  }

  return `Nästa leverans (nr ${plan.next.sequence}) skapas automatiskt ${plan.planned_on}.`
}
